/**
 * EventDay — the on-site ("day-of") surface inside Studio. Shown to Event
 * Coordinators, Event Managers and Admins (gated on onsite.* capabilities).
 *
 * DATA SOURCE (2026-07-10 ingest — Greg's design on this master's op spine):
 *   • Events    → useEvents() (typed api-client → adapter). Filtered to on-site
 *                 relevance (live + upcoming) and bucketed Today / This week
 *                 from the event's own start time.
 *   • Attendees → useGuests({ eventId }) — the event's registered guests.
 *   • Queue     → listQueue(eventId), fanned out over LIVE events only (CD-6).
 *   • WRITES are AWAITED typed ops (no-fake-interactivity): check-in/undo →
 *     checkInGuest / updateQueueEntry, walk-up → addWalkup, guest edit →
 *     updateGuest, message → sendOnsiteMessage, queue → updateQueueEntry.
 *     Local caches update only after the op resolves.
 *
 * ONE responsive page (no device frame):
 *   • phone (≤640): single-column stack — event list → event → sub-views.
 *   • tablet/desktop (>640): master–detail — persistent event list left, live
 *     event console right. Fills the shell's main area edge-to-edge.
 * QR check-in is front-and-centre on every device (full-screen scanner overlay).
 * Studio-purple chrome. Every action is capability-gated.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { can, useUser } from "../../shell/access";
import { useViewport } from "../../shell/useResponsive";
import { listQueue, checkInGuest, updateQueueEntry, addWalkup, sendOnsiteMessage, updateGuest } from "@v0y4ge/api-client";
import { useEvents } from "../events/useEvents";
import { useGuests } from "../guests/useGuests";
import { useTicketTypes } from "../tickets/ticket-types-store";
import { useQuestions, QTYPE, type Question } from "../questions/useQuestions";
import { useMessaging } from "../messaging/useMessaging";
import type { UIEvent } from "../../lib/events-adapter";
import { OnsiteApp } from "./OnsiteApps";
import { useT, t as i18nT } from "../../lib/i18n";

/* ── Types ──────────────────────────────────────────────────────────── */
type Attendee = { id: string; name: string; tier: string; color: string; checked: boolean; walkin?: boolean; status?: string };
const COLORS = ["#5142C7", "#1F8A5B", "#C77F14", "#C13584", "#2D60EB", "#0E7490", "#8955F2", "#D01647"];
const colorFor = (name: string) => COLORS[(name || "?").length % COLORS.length];
// ui-config — mints an EPHEMERAL client id for a walk-in row the user just
// created against the real backend (invoked only after `await addWalkup(...)`);
// never seeds initial state. Date.now()-based id generation per Claude Code
// docket 2026-07-13 (seed-guard `math-random` false-positive; refactor option).
const mkA = (name: string, tier: string, checked: boolean, walkin = false): Attendee =>
  ({ id: "walkin-" + name.replace(/\s/g, "").toLowerCase() + "-" + Date.now().toString(36), name, tier, color: colorFor(name), checked, walkin });

/* Will-call queue — REAL rows from listQueue (no seed). */
type QueueEntry = { id: string; eventId?: string; name: string; party: number; waited: number; status: "waiting" | "serving" | "done"; express?: boolean };
function toQueueEntry(r: Record<string, any>, eventId?: string): QueueEntry {
  return { id: String(r.id), eventId: eventId ?? (r.eventId ? String(r.eventId) : undefined), name: String(r.name ?? r.guestName ?? ""), party: Number(r.party ?? r.partySize) || 1, waited: Number(r.waited ?? r.waitedMinutes) || 0, status: (r.status === "serving" || r.status === "done") ? r.status : "waiting", express: !!r.express };
}

const ONSITE_APPS = [ // ui-config — static catalog of on-site app TYPES, not tenant data
  { id: "poll", label: "Poll", desc: "Live audience polls", glyph: "📊" },
  { id: "waiver", label: "Waiver", desc: "Capture signatures", glyph: "✍️" },
  { id: "read", label: "Read Pass", desc: "Scan badges / passes", glyph: "🎫" },
  { id: "pickup", label: "Product Pickup", desc: "Hand-off & collect", glyph: "📦" },
  { id: "access", label: "Access Control", desc: "Door / zone entry", glyph: "🚪" },
  { id: "activation", label: "Activation", desc: "Brand activations", glyph: "✨" },
  { id: "session", label: "Session check-in", desc: "Check into sessions", glyph: "🗓️" },
];

/* ── Studio-purple chrome ────────────────────────────────────────────── */
const SANS = "var(--jrni-font-family-sans)";
const ACCENT = T.p1;
const ACCENT_DK = T.p1;
const ACCENT_SOFT = T.p4;
const ACCENT_BORDER = T.p3;

function Chev({ dir = "right", size = 14, color = T.n2 }: { dir?: "right" | "left"; size?: number; color?: string }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d={dir === "left" ? "m10 4-4 4 4 4" : "m6 4 4 4-4 4"} /></svg>;
}
function StatusDot({ children }: { children: React.ReactNode }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: T.green1 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: T.green1 }} />{children}</span>;
}
function Meta({ icon, children }: { icon: string; children: React.ReactNode }) {
  return <li style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13.5, color: T.n1, lineHeight: 1.55 }}><span style={{ width: 15, textAlign: "center", flexShrink: 0 }}>{icon}</span><span>{children}</span></li>;
}
function Pill({ tone, children }: { tone: "success" | "warn" | "brand" | "neutral" | "danger"; children: React.ReactNode }) {
  const map = { success: [T.green2, T.green1], warn: [T.orange2, T.orange1], brand: [ACCENT_SOFT, ACCENT_DK], neutral: [T.n6, T.soft], danger: [T.red2, T.red1] } as const;
  const [bg, fg] = map[tone];
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: bg, color: fg, whiteSpace: "nowrap" }}>{children}</span>;
}
function Avatar({ name, color, size = 36 }: { name: string; color: string; size?: number }) {
  const AVATAR_GLYPH_RATIO = 0.36; // ui-config — initials glyph scales with avatar diameter (presentational)
  const init = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return <span style={{ width: size, height: size, borderRadius: 999, background: color, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: size * AVATAR_GLYPH_RATIO, fontWeight: 600, flexShrink: 0, fontFamily: SANS }}>{init}</span>;
}
function Topbar({ title, onBack, right }: { title?: string; onBack?: () => void; right?: React.ReactNode }) {
  const t = useT();
  return (
    <div style={{ background: "#fff", padding: "8px 14px", minHeight: 56, boxSizing: "border-box", borderBottom: `1px solid ${T.borderStrong}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexShrink: 0, zIndex: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
        {onBack ? <button onClick={onBack} aria-label={t("common.actions.back", "Back")} style={{ width: 34, height: 34, border: "none", background: "transparent", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, flexShrink: 0 }}><Chev dir="left" size={18} color={T.n1} /></button> : null}
        {title ? <span style={{ fontSize: 16, fontWeight: 700, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span> : null}
      </div>
      {right}
    </div>
  );
}
function QrGlyph({ size = 20 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><path d="M14 14h3v3h-3z M20 20h1v1h-1z" /></svg>;
}
function ScanBtn({ onClick }: { onClick: () => void }) {
  const t = useT();
  return (
    <button onClick={onClick} aria-label={t("eventday.eventDay.scanQr", "Scan QR")} title={t("eventday.eventDay.scanToCheckIn", "Scan to check in")} style={{ width: 40, height: 40, borderRadius: 10, border: "none", cursor: "pointer", background: ACCENT, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <QrGlyph />
    </button>
  );
}
const PBTN = (variant: "primary" | "secondary" = "primary") => ({
  height: 44, borderRadius: 8, border: variant === "primary" ? "none" : `1px solid ${ACCENT_BORDER}`,
  background: variant === "primary" ? ACCENT : "#fff", color: variant === "primary" ? "#fff" : ACCENT_DK,
  fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: SANS,
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "0 16px",
} as const);

/* ── On-site relevance + date bucketing (from the real event) ─────────── */
type Bucket = "today" | "week" | null;
function eventBucket(e: UIEvent): Bucket {
  if (e.status === "past" || e.status === "canceled" || e.status === "draft") return null;
  if (e.status === "live") return "today";
  const iso = e.raw?.startsAt;
  if (!iso) return e.status === "upcoming" ? "week" : null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "week";
  const s = new Date(); s.setHours(0, 0, 0, 0);
  const dayEnd = s.getTime() + 864e5 - 1;
  if (t <= dayEnd) return "today";                 // starts today (or already started)
  if (t <= s.getTime() + 7 * 864e5) return "week"; // within the next 7 days
  return null;
}

/* ═══ Main ═══════════════════════════════════════════════════════════ */
export default function EventDay() {
  const t = useT();
  const u = useUser();
  const firstName = (u.name || "there").split(" ")[0];
  const vp = useViewport();
  const twoPane = vp !== "phone";

  const { events: allEvents, loading: eventsLoading } = useEvents();
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [checkinBusy, setCheckinBusy] = useState<string | null>(null);
  const [scene, setScene] = useState<"dashboard" | "event" | "occupancy" | "apps" | "app" | "message" | "queue">("dashboard");
  const [detail, setDetail] = useState<"attendees" | "occupancy" | "apps" | "app" | "message" | "queue">("attendees");
  const [appId, setAppId] = useState("poll");
  const [scope, setScope] = useState<"today" | "week">("today");
  const [openId, setOpenId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [walkupOpen, setWalkupOpen] = useState(false);
  const [checkinOpen, setCheckinOpen] = useState(false);

  // Optimistic client overlays (no on-site write contract yet).
  const [checkOverride, setCheckOverride] = useState<Record<string, boolean>>({});
  const [walkins, setWalkins] = useState<Record<string, Attendee[]>>({});
  const [detailId, setDetailId] = useState<string | null>(null);
  const [guestEdits, setGuestEdits] = useState<Record<string, { name?: string; email?: string; phone?: string; company?: string; ticket?: string; answers?: Record<string, any> }>>({});

  const flash = (m: string) => { setToast(m); setTimeout(() => setToast(null), 2200); };

  // Will-call queue — fetched via listQueue, fanned out per LIVE event only
  // (CD-6/CD-critical: the op requires a real eventId; never the full catalog).
  useEffect(() => {
    let live = true;
    const onsite = allEvents.filter((e) => e.status === "live");
    if (!onsite.length) { setQueue([]); return; }
    Promise.all(onsite.map((e) => listQueue(e.id).then((r: any) => ({ e, r })).catch((err: unknown) => { console.warn(`listQueue(${e.id}) failed:`, err); return null; })))
      .then((results) => {
        if (!live) return;
        const rows: QueueEntry[] = [];
        for (const it of results) {
          if (!it) continue;
          const arr = ((it.r as any)?.queue ?? (it.r as any)?.data ?? (it.r as any)?.entries ?? (Array.isArray(it.r) ? it.r : [])) as Record<string, any>[];
          arr.forEach((q) => rows.push(toQueueEntry(q, it.e.id)));
        }
        setQueue(rows);
      });
    return () => { live = false; };
  }, [allEvents]);

  // ── Events: filter to on-site relevance, bucket by date ──
  const buckets = useMemo(() => {
    const today: UIEvent[] = []; const week: UIEvent[] = [];
    for (const e of allEvents) {
      const b = eventBucket(e);
      if (b === "today") { today.push(e); week.push(e); }
      else if (b === "week") week.push(e);
    }
    return { today, week };
  }, [allEvents]);
  const shown = scope === "today" ? buckets.today : buckets.week;

  // Keep a valid selection as data loads / scope changes.
  useEffect(() => {
    if (!shown.length) return;
    if (!openId || !shown.some((e) => e.id === openId)) setOpenId(shown[0].id);
  }, [shown, openId]);
  const ev = allEvents.find((e) => e.id === openId) || shown[0] || null;

  // ── Attendees for the selected event (real guests + overlays) ──
  const { guests, loading: guestsLoading } = useGuests({ eventId: openId ?? undefined });
  const guestChecked = (g: { id: string; status?: string; checked?: string }) => checkOverride[g.id] ?? (g.status === "checked-in" || !!g.checked);
  const attendees: Attendee[] = ev ? [
    ...(walkins[ev.id] ?? []).map((a) => { const e = guestEdits[a.id]; return e ? { ...a, name: e.name ?? a.name, tier: e.ticket ?? a.tier } : a; }),
    ...guests.map((g) => { const e = guestEdits[g.id]; return { id: g.id, name: e?.name ?? g.name, tier: e?.ticket ?? g.ticket ?? "General", color: g.color || colorFor(g.name), checked: guestChecked(g), walkin: (g.source || "").toLowerCase().includes("walk"), status: g.status }; }),
  ] : [];
  const attLoading = guestsLoading && (walkins[openId ?? ""] ?? []).length === 0;
  const selChecked = attendees.filter((a) => a.checked).length;
  // Counts use REGISTERED semantics — exactly the statuses the event DTO's
  // registeredCount counts (registered + checked-in, per the worker's
  // loadEventGuestCounts / the mocks' applyGuestDerivedCounts). Waitlist,
  // cancelled, no-show, invited rows stay VISIBLE in the roster (status pill)
  // but don't inflate the denominator — selecting a card must never change
  // its count (Leadership Forum read 0/3 selected vs 0/2 unselected: the
  // roster includes a cancelled guest the aggregate doesn't).
  const onRoll = (a: Attendee) => a.checked || !a.status || a.status === "registered" || a.status === "checked-in";
  const selTotal = attendees.filter(onRoll).length;

  // Check-in / undo — AWAITED against the real ops (no-fake-interactivity);
  // local caches reflect the server-confirmed state only after resolve.
  // Returns whether the transition persisted (the scanner path needs to know).
  const toggleCheck = async (aid: string): Promise<boolean> => {
    if (!ev || checkinBusy) return false;
    const a = attendees.find((x) => x.id === aid);
    const wasChecked = !!a?.checked;
    const wk = (walkins[ev.id] ?? []).find((x) => x.id === aid);
    setCheckinBusy(aid);
    try {
      if (wasChecked) await updateQueueEntry(ev.id, aid, { status: "registered" });
      else await checkInGuest(ev.id, aid);
      if (wk) setWalkins((m) => ({ ...m, [ev.id]: (m[ev.id] ?? []).map((x) => x.id === aid ? { ...x, checked: !wasChecked } : x) }));
      else setCheckOverride((m) => ({ ...m, [aid]: !wasChecked }));
      flash(wasChecked ? `${a?.name ?? "Guest"} un-checked` : `${a?.name ?? "Guest"} checked in`);
      return true;
    } catch (err) {
      console.warn("check-in failed:", err);
      flash("Couldn't update check-in. Retry in a moment.");
      return false;
    } finally { setCheckinBusy(null); }
  };
  // Walk-up — AWAITED addWalkup first; the row is cached only after the server
  // accepts it. email/phone/answers ride the same call (drop29). #821 (2026-07-14
  // docket #10): the follow-up check-in MUST use the REAL id from the 201
  // response — never the client temp id (a PATCH on "walkin-…" 404s, leaving the
  // guest registered-but-not-checked-in behind an optimistic "✓ In"). The row
  // renders checked only after checkInGuest(realId) RESOLVES; if the id is
  // missing or the check-in fails, the guest lands as Registered — honestly.
  const addWalkin = async (name: string, tier: string, extra?: { email?: string; phone?: string; answers?: Record<string, any> }): Promise<"checked" | "added" | null> => {
    if (!ev) return null;
    const [firstName, ...rest] = name.split(" ");
    const lastName = rest.join(" ") || "—";
    let created: Record<string, any> | null = null;
    try {
      created = await addWalkup({ eventId: ev.id, firstName, lastName, tier_id: tier, email: extra?.email || undefined, phone: extra?.phone || undefined, answers: extra?.answers && Object.keys(extra.answers).length ? extra.answers : undefined } as never) as Record<string, any>;
    } catch (err) {
      console.warn("addWalkup failed:", err);
      flash("Couldn't add the guest. Retry in a moment.");
      return null;
    }
    const realId = created?.guest?.id ?? created?.id ?? created?.data?.id;
    let checked = false;
    if (realId) {
      try { await checkInGuest(ev.id, String(realId)); checked = true; }
      catch (err) { console.warn("walk-up check-in failed (guest was created):", err); }
    } else {
      console.warn("addWalkup response carried no guest id — skipping check-in:", created);
    }
    const a = { ...mkA(name, tier, checked, true), ...(realId ? { id: String(realId) } : {}) };
    setWalkins((m) => ({ ...m, [ev.id]: [a, ...(m[ev.id] ?? [])] }));
    if (extra) setGuestEdits((m) => ({ ...m, [a.id]: { email: extra.email, phone: extra.phone, answers: extra.answers } }));
    return checked ? "checked" : "added";
  };

  // Shared Add-Guest submit — the toast reflects what actually persisted.
  const onAddGuest = async (name: string, tier: string, extra: { email: string; phone: string; answers: Record<string, any> }) => {
    const res = await addWalkin(name, tier, extra);
    if (!res) return; // create failed — modal stays open, error already flashed
    setWalkupOpen(false);
    flash(res === "checked" ? `${name} added & checked in` : `${name} registered — check-in didn't stick. Tap Check in to retry.`);
  };

  // Resolve the full record behind an attendee (real guest or walk-in) + edits.
  const resolveGuest = (id: string) => {
    const e = guestEdits[id] || {};
    const wk = ev ? (walkins[ev.id] ?? []).find((a) => a.id === id) : null;
    if (wk) return { id, name: e.name ?? wk.name, email: e.email ?? "", phone: e.phone ?? "", company: e.company ?? "", ticket: e.ticket ?? wk.tier, status: wk.checked ? "checked-in" : "registered", checked: wk.checked ? "On arrival" : "", source: "Walk-in", walkin: true, color: wk.color, answers: e.answers ?? {} };
    const g = guests.find((x) => x.id === id);
    if (!g) return null;
    return { id, name: e.name ?? g.name, email: e.email ?? g.email, phone: e.phone ?? "", company: e.company ?? g.company ?? "", ticket: e.ticket ?? g.ticket ?? "General", status: guestChecked(g) ? "checked-in" : "registered", checked: g.checked ?? "", source: g.source, walkin: false, color: g.color || colorFor(g.name), answers: e.answers ?? {} };
  };
  // Guest edits — awaited updateGuest for registered guests (walk-ins remain
  // session records until the walk-up create returns a patchable id). Edited
  // booking answers ride the SAME call as customFields.answers (drop29 #11).
  const saveGuest = async (id: string, patch: Record<string, any>): Promise<boolean> => {
    const isWalkin = ev ? !!(walkins[ev.id] ?? []).find((a) => a.id === id) : false;
    if (!isWalkin) {
      try {
        const [firstName, ...rest] = String(patch.name || "").split(" ");
        if (typeof updateGuest === "function") await updateGuest(id, { firstName, lastName: rest.join(" "), email: patch.email, phone: patch.phone, organization: patch.company, ticketTier: patch.ticket, customFields: patch.answers && Object.keys(patch.answers).length ? { answers: patch.answers } : undefined } as never);
      } catch (err) {
        console.warn("updateGuest failed:", err);
        flash("Couldn't save the guest. Retry in a moment.");
        return false;
      }
    }
    setGuestEdits((m) => ({ ...m, [id]: { ...(m[id] || {}), ...patch } }));
    return true;
  };

  const q = search.trim().toLowerCase();

  // Navigation helpers — same intent, per-layout target.
  const openEvent = (id: string) => { setOpenId(id); setSearch(""); if (twoPane) setDetail("attendees"); else setScene("event"); };
  const go = (t: "occupancy" | "apps" | "app" | "message" | "queue") => { if (twoPane) setDetail(t); else setScene(t); };
  const backToEvent = () => { if (twoPane) setDetail("attendees"); else setScene("event"); };
  const openQueue = () => { if (twoPane) setDetail("queue"); else setScene("queue"); };

  // #3 (2026-07-14 docket, voyage#70) — real scan resolution. The declared
  // checkInByCode(eventId, code) client op hasn't shipped (deprioritized by
  // operator ruling), so a decoded/typed code resolves against the LOADED guest
  // list (guest id or registered email) and checks in through the real
  // checkInGuest op. An unmatched code is a hard failure state — never a fake
  // success. When checkInByCode lands, this resolver collapses to one client call.
  const checkInByScannedCode = async (code: string): Promise<{ ok: boolean; text: string }> => {
    const norm = code.trim();
    if (!norm || !ev) return { ok: false, text: "Ticket not recognized." };
    const byId = attendees.find((x) => x.id === norm);
    const byEmail = !byId && norm.includes("@") ? guests.find((g) => (g.email || "").toLowerCase() === norm.toLowerCase()) : undefined;
    const a = byId || (byEmail ? attendees.find((x) => x.id === byEmail.id) : undefined);
    if (!a) return { ok: false, text: "Ticket not recognized — check the code, or find the guest in the list." };
    if (a.checked) return { ok: false, text: `${a.name} is already checked in.` };
    const ok = await toggleCheck(a.id);
    return ok ? { ok: true, text: `${a.name} checked in` } : { ok: false, text: "Couldn't check in. Retry in a moment." };
  };

  /* ── Reusable content blocks ───────────────────────────────────────── */
  const progressStrip = ev ? (
    <div style={{ background: "#fff", padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{selChecked}/{selTotal} {t("eventday.eventDay.checkedInee97683d", "checked-in")}</span>
        {ev.status === "live" ? <StatusDot>{t("common.status.live", "Live")}</StatusDot> : null}
      </div>
      <div style={{ height: 6, borderRadius: 10, background: T.green2, overflow: "hidden", border: `1px solid ${T.green1}` }}>
        <div style={{ width: `${Math.round((selChecked / Math.max(1, selTotal)) * 100)}%`, height: "100%", background: T.green1 }} />
      </div>
    </div>
  ) : null;

  const scanBanner = can("guests.checkin") ? (
    <button onClick={() => setCheckinOpen(true)} style={{ width: "100%", textAlign: "left", border: "none", cursor: "pointer", borderRadius: 12, padding: 16, background: ACCENT, color: "#fff", display: "flex", alignItems: "center", gap: 14, fontFamily: SANS, boxShadow: "0 6px 16px rgba(81,66,199,0.28)" }}>
      <span style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(255,255,255,0.18)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><QrGlyph size={24} /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 16, fontWeight: 700 }}>{t("eventday.eventDay.scanToCheckIn", "Scan to check in")}</span>
        <span style={{ display: "block", fontSize: 12.5, opacity: 0.9, marginTop: 1 }}>{t("eventday.eventDay.pointTheCameraAtAGuestSQrTicketOrWalletPass", "Point the camera at a guest's QR ticket or wallet pass")}</span>
      </span>
      <Chev color="#fff" />
    </button>
  ) : null;

  const actionToolbar = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {can("onsite.walkup") ? <button onClick={() => setWalkupOpen(true)} style={{ ...PBTN("secondary"), height: 40 }}>{t("eventday.eventDay.addGuest", "Add Guest")}</button> : null}
      {can("campaigns.send") ? <button onClick={() => go("message")} style={{ ...PBTN("secondary"), height: 40 }}>{t("eventday.eventDay.message", "Message")}</button> : null}
      {can("onsite.occupancy") ? <button onClick={() => go("occupancy")} style={{ ...PBTN("secondary"), height: 40 }}>{t("eventday.eventDay.occupancy", "Occupancy")}</button> : null}
      {can("onsite.apps.run") ? <button onClick={() => go("apps")} style={{ ...PBTN("secondary"), height: 40 }}>{t("eventday.eventDay.runAnApp", "Run an app")}</button> : null}
      {can("onsite.queue") ? <button onClick={openQueue} style={{ ...PBTN("secondary"), height: 40 }}>{t("eventday.eventDay.willCallQueue", "Will-call queue")}</button> : null}
    </div>
  );

  const filtered = attendees.filter((a) => !q || a.name.toLowerCase().includes(q));
  const attendeeList = (
    <>
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("eventday.eventDay.searchAttendees", "Search attendees…")} style={{ width: "100%", height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 14, fontFamily: SANS, color: T.n1, boxSizing: "border-box", outline: "none" }} />
      <div style={{ display: "grid", gridTemplateColumns: twoPane ? "repeat(auto-fill, minmax(280px, 1fr))" : "1fr", gap: 8 }}>
        {filtered.map((a) => (
          <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, background: "#fff", borderRadius: 10, border: `1px solid ${T.border}` }}>
            <button onClick={() => setDetailId(a.id)} title={t("eventday.eventDay.viewGuestDetails", "View guest details")} style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, border: "none", background: "transparent", padding: 0, cursor: "pointer", fontFamily: SANS, textAlign: "left" }}>
              <Avatar name={a.name} color={a.color} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.n1, display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{a.name}</span>{a.walkin ? <Pill tone="warn">{t("eventday.eventDay.walkIn", "Walk-in")}</Pill> : null}</div>
                <div style={{ fontSize: 12, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.tier}</div>
              </div>
            </button>
            {can("guests.checkin") ? (
              <button disabled={checkinBusy === a.id} onClick={() => { void toggleCheck(a.id); }} style={{ height: 34, padding: "0 14px", borderRadius: 999, border: `1.5px solid ${a.checked ? T.green1 : T.n4}`, background: a.checked ? T.green2 : "#fff", color: a.checked ? T.green1 : T.n2, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: SANS, display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0, opacity: checkinBusy === a.id ? 0.6 : 1 }}>
                {a.checked ? t("eventday.eventDay.in9ff32cfd", "✓ In") : t("eventday.eventDay.checkIn", "Check in")}
              </button>
            ) : <Pill tone={a.checked ? "success" : "neutral"}>{a.checked ? t('eventday.eventDay.text9') : a.status === "waitlist" ? t('eventday.eventDay.text10') : (a.status === "cancelled" || a.status === "canceled") ? t('eventday.eventDay.text11') : a.status === "no-show" ? t('eventday.eventDay.text12') : a.status === "invited" ? t('eventday.eventDay.text13') : t('eventday.eventDay.text14')}</Pill>}
          </div>
        ))}
        {filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>
            {attLoading ? t('eventday.eventDay.text15') : q ? t('eventday.eventDay.text16', { search }) : t('eventday.eventDay.text17')}
          </div>
        ) : null}
      </div>
    </>
  );

  const eventWorkBody = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 14 }}>
      {scanBanner}
      {actionToolbar}
      {attendeeList}
    </div>
  );

  const occ = selChecked;
  const cap = ev?.capacity || 0;
  const occupancyBody = ev ? (
    <div style={{ padding: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
      <div style={{ fontSize: 13, color: T.soft, textAlign: "center" }}>{ev.title}</div>
      <div style={{ width: 180, height: 180, borderRadius: 999, border: `10px solid ${cap && occ / cap > 0.85 ? T.red1 : T.green1}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#fff" }}>
        <div style={{ fontSize: 48, fontWeight: 800, color: T.n1, lineHeight: 1 }}>{occ}</div>
        <div style={{ fontSize: 13, color: T.soft, marginTop: 4 }}>{t("eventday.eventDay.of", "of")}{" "}{cap || "∞"}</div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: cap && occ / cap > 0.85 ? T.red1 : T.green1 }}>{cap ? /* TODO(i18n): replace template-literal with t('eventday.eventDay.text18', { ... }) */`${Math.round((occ / cap) * 100)}% full` : t('eventday.eventDay.text19', { occ })}</div>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <button onClick={() => { const t = attendees.find((a) => a.checked); if (t) toggleCheck(t.id); }} style={{ width: 56, height: 56, borderRadius: 999, border: `1.5px solid ${T.n4}`, background: "#fff", fontSize: 24, cursor: "pointer", color: T.n1 }}>−</button>
        <button onClick={() => { const t = attendees.find((a) => !a.checked); if (t) toggleCheck(t.id); }} style={{ width: 56, height: 56, borderRadius: 999, border: "none", background: ACCENT, fontSize: 24, cursor: "pointer", color: "#fff" }}>+</button>
      </div>
      <div style={{ fontSize: 12, color: T.soft }}>{t("eventday.eventDay.manualAdjustForGuestsEnteringLeaving", "Manual adjust for guests entering / leaving")}</div>
    </div>
  ) : null;

  const appsBody = ev ? (
    <div style={{ padding: 14 }}>
      <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 12 }}>{t("eventday.eventDay.onSiteAppsFor", "On-site apps for")}{" "}{ev.title}{t("eventday.eventDay.managersConfigureWhichAppsStaffCanRun", ". Managers configure which apps staff can run.")}</div>
      <div style={{ display: "grid", gridTemplateColumns: twoPane ? "repeat(auto-fill, minmax(200px, 1fr))" : "1fr 1fr", gap: 10 }}>
        {ONSITE_APPS.map((a) => (
          <button key={a.id} onClick={() => { setAppId(a.id); go("app"); }} style={{ padding: 16, background: "#fff", borderRadius: 12, border: `1px solid ${T.border}`, cursor: "pointer", textAlign: "left", fontFamily: SANS, display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontSize: 28 }}>{a.glyph}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{a.label}</span>
            <span style={{ fontSize: 11.5, color: T.soft }}>{a.desc}</span>
          </button>
        ))}
      </div>
    </div>
  ) : null;

  // Queue actions — "Done" persists through the REAL guest-status op (a served
  // will-call guest is CHECKED IN). "Call" now ALSO persists: drop29 added a
  // `serving` cue to updateQueueEntry (status optional), so the now-serving
  // state survives reload and broadcasts to other coordinator devices.
  const setQ = async (id: string, status: QueueEntry["status"]) => {
    const entry = queue.find((c) => c.id === id);
    const eid = entry?.eventId ?? ev?.id;
    try {
      if (status === "serving") { if (eid) await updateQueueEntry(eid, id, { serving: true } as never); }
      else if (eid) await updateQueueEntry(eid, id, { status: "checked-in" });
      setQueue((qs) => qs.map((c) => c.id === id ? { ...c, status } : c));
      flash(status === "serving" ? `Now serving ${entry?.name ?? "guest"}` : `${entry?.name ?? "Guest"} served`);
    } catch (err) {
      console.warn("updateQueueEntry failed:", err);
      flash("Couldn't update the queue. Retry in a moment.");
    }
  };
  const queueBody = (
    <div style={{ padding: "12px 12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
      {queue.filter((c) => c.status !== "done").map((c) => (
        <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, background: "#fff", borderRadius: 10, border: `1px solid ${c.status === "serving" ? ACCENT : T.border}` }}>
          <Avatar name={c.name} color={colorFor(c.name)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.n1, display: "flex", alignItems: "center", gap: 6 }}>{c.name}{c.express ? <Pill tone="brand">{t("eventday.eventDay.express", "Express")}</Pill> : null}</div>
            <div style={{ fontSize: 12, color: T.soft }}>{t("eventday.eventDay.partyOf", "Party of")}{" "}{c.party} {t("eventday.eventDay.waited", "· waited")}{" "}{c.waited}{t("eventday.eventDay.m", "m")}</div>
          </div>
          {c.status === "waiting" ? <button onClick={() => { void setQ(c.id, "serving"); }} style={{ ...PBTN("primary"), height: 34, fontSize: 13 }}>{t("eventday.eventDay.call", "Call")}</button>
            : <button onClick={() => { void setQ(c.id, "done"); }} style={{ ...PBTN("secondary"), height: 34, fontSize: 13 }}>{t("common.actions.done", "Done")}</button>}
        </div>
      ))}
      {queue.filter((c) => c.status !== "done").length === 0 ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("eventday.eventDay.queueIsClear", "Queue is clear 🎉")}</div> : null}
    </div>
  );

  const messageBody = ev ? <MessageBody eventId={ev.id} attendees={attendees} onSent={(n) => { flash(`Message sent to ${n} guest${n === 1 ? "" : "s"}`); backToEvent(); }} /> : null;
  const appBody = ev ? <OnsiteApp appId={appId} eventTitle={ev.title} onBack={() => go("apps")} flash={flash} /> : null;
  const subTitle = (d: string) => d === "occupancy" ? "Occupancy" : d === "message" ? "Message guests" : d === "apps" ? "Run an app" : d === "queue" ? "Will-call queue" : (ev?.title ?? "");

  /* ── Event list (persistent left pane / phone dashboard) ──────────── */
  const eventListInner = (
    <>
      <div style={{ padding: "14px 16px 4px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: T.soft }}>{t("eventday.eventDay.rightNow", "Right now")}</div>
        <h1 style={{ margin: "2px 0 0", fontFamily: SANS, fontSize: twoPane ? 20 : 23, fontWeight: 700, color: T.n1, lineHeight: 1.2 }}>{t("eventday.eventDay.goodMorning", "Good morning,")}{" "}{firstName}.</h1>
      </div>
      <div style={{ padding: "10px 16px 2px" }}>
        <div style={{ display: "inline-flex", padding: 3, background: ACCENT_SOFT, border: `1px solid ${ACCENT_BORDER}`, borderRadius: 999, gap: 3 }}>
          {([["today", `Today · ${buckets.today.length}`], ["week", `This week · ${buckets.week.length}`]] as const).map(([id, label]) => {
            const sel = scope === id;
            return <button key={id} onClick={() => setScope(id)} style={{ padding: "6px 16px", border: "none", borderRadius: 999, cursor: "pointer", background: sel ? "#fff" : "transparent", color: ACCENT_DK, fontSize: 13, fontWeight: sel ? 700 : 500, fontFamily: SANS, boxShadow: sel ? "0 1px 2px rgba(16,24,40,0.08)" : "none", opacity: sel ? 1 : 0.7 }}>{label}</button>;
          })}
        </div>
      </div>
      <div style={{ padding: "12px 12px 16px", display: "grid", gridTemplateColumns: twoPane ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
        {shown.map((e) => {
          const active = twoPane && e.id === openId && detail !== "queue";
          const isOpen = e.id === openId;
          // Focused event mirrors the live console (fetched guest list) once
          // it's loaded AND has rows; otherwise the event's own aggregate counts
          // from the list DTO. Previously an empty/loading roster made `reg` 0,
          // and the `reg || e.capacity` display fallback swapped the denominator
          // to CAPACITY on click ("0 of 25" selected vs "0 of 18" not — #operator
          // report 2026-07-14). Events with no fetched roster now keep their DTO
          // numbers, so selecting a card never changes its count.
          const mirror = isOpen && !guestsLoading && selTotal > 0;
          const reg = mirror ? selTotal : (e.registered || 0);
          const chk = mirror ? selChecked : (e.checked || 0);
          return (
            <button key={e.id} onClick={() => openEvent(e.id)} style={{ width: "100%", textAlign: "left", padding: 13, background: active ? ACCENT_SOFT : "#fff", borderRadius: 10, border: `1px solid ${active ? ACCENT : T.border}`, boxShadow: active ? "none" : T.shadowMd, cursor: "pointer", display: "flex", flexDirection: "column", gap: 8, fontFamily: SANS, position: "relative" }}>
              {active ? <span style={{ position: "absolute", left: 0, top: 12, bottom: 12, width: 3, borderRadius: 999, background: ACCENT }} /> : null}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: T.n1 }}>{e.title}</h3>
                {e.status === "live" ? <StatusDot>{t("common.status.live", "Live")}</StatusDot> : null}
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                <Meta icon="📅">{e.date}{e.time ? /* TODO(i18n): replace template-literal with t('eventday.eventDay.text20', { ... }) */` · ${e.time}` : ""}</Meta>
                <Meta icon="👤">{e.host}</Meta>
                <Meta icon="📍">{e.location}</Meta>
              </ul>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: T.n1, marginBottom: 5 }}>{chk}/{reg || e.capacity || 0} {t("eventday.eventDay.checkedInee97683d", "checked-in")}</div>
                <div style={{ height: 5, borderRadius: 10, background: T.green2, overflow: "hidden", border: `1px solid ${T.green1}` }}>
                  <div style={{ width: `${Math.round((chk / Math.max(1, reg || e.capacity || 1)) * 100)}%`, height: "100%", background: T.green1 }} />
                </div>
              </div>
            </button>
          );
        })}
        {shown.length === 0 ? <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13.5 }}>{eventsLoading ? t('eventday.eventDay.text21') : /* TODO(i18n): replace template-literal with t('eventday.eventDay.text22', { ... }) */`No events ${scope === "today" ? "today" : "this week"}.`}</div> : null}
      </div>
      {can("onsite.queue") && queue.some((c) => c.status === "waiting") ? (
        <div style={{ padding: "0 12px 16px" }}>
          <button onClick={openQueue} style={{ width: "100%", textAlign: "left", padding: 13, background: twoPane && detail === "queue" ? ACCENT_SOFT : "#fff", borderRadius: 10, border: `1px solid ${twoPane && detail === "queue" ? ACCENT : T.border}`, boxShadow: twoPane && detail === "queue" ? "none" : T.shadowMd, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, fontFamily: SANS }}>
            <span style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, background: T.orange2, color: T.orange1, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>👥</span>
            <span style={{ flex: 1 }}>
              <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: T.n1 }}>{t("eventday.eventDay.willCallQueue", "Will-call queue")}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: T.soft, marginTop: 2 }}><span style={{ width: 6, height: 6, borderRadius: 999, background: T.orange1 }} />{queue.filter((c) => c.status === "waiting").length} {t("eventday.eventDay.waitingLongest", "waiting · longest")}{" "}{Math.max(...queue.map((c) => c.waited))}{t("eventday.eventDay.m", "m")}</span>
            </span>
            <Chev />
          </button>
        </div>
      ) : null}
    </>
  );

  const emptyDetail = (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40, textAlign: "center" }}>
      <div style={{ color: T.soft, fontSize: 14, maxWidth: 260, fontFamily: SANS }}>{eventsLoading ? t('eventday.eventDay.text21') : t('eventday.eventDay.text23')}</div>
    </div>
  );

  /* ══ Desktop / tablet: master–detail ══════════════════════════════ */
  if (twoPane) {
    const rightIsSub = detail !== "attendees" && detail !== "queue";
    const rightBody = detail === "occupancy" ? occupancyBody : detail === "message" ? messageBody : detail === "apps" ? appsBody : detail === "app" ? appBody : detail === "queue" ? queueBody : eventWorkBody;
    return (
      <Screen rail="eventday" topbar>
        <div style={{ flex: 1, minHeight: 0, display: "flex", background: "#fff" }}>
          <aside style={{ width: vp === "desktop" ? 360 : 300, flexShrink: 0, borderRight: `1px solid ${T.border}`, overflowY: "auto", background: T.canvas, minHeight: 0 }}>
            {eventListInner}
          </aside>
          <section style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
            {detail === "queue" ? (
              <>
                <div style={{ background: "#fff", padding: "14px 18px", borderBottom: `1px solid ${T.borderStrong}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: T.n1 }}>{t("eventday.eventDay.willCallQueue", "Will-call queue")}</div>
                  {can("onsite.walkup") ? <button onClick={() => setWalkupOpen(true)} disabled={!ev} style={{ ...PBTN("primary"), height: 36, fontSize: 13, opacity: ev ? 1 : 0.5 }}>{t("eventday.eventDay.addGuest", "Add Guest")}</button> : null}
                </div>
                <div style={{ flex: 1, overflowY: "auto", background: T.canvas, minHeight: 0 }}>{queueBody}</div>
              </>
            ) : !ev ? emptyDetail : (
              <>
                <div style={{ background: "#fff", padding: "12px 18px", borderBottom: `1px solid ${T.borderStrong}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {rightIsSub ? <button onClick={backToEvent} aria-label={t("eventday.eventDay.backToAttendees", "Back to attendees")} style={{ width: 30, height: 30, border: `1px solid ${T.border}`, borderRadius: 8, background: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Chev dir="left" size={15} color={T.n1} /></button> : null}
                      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rightIsSub ? subTitle(detail) : ev.title}</h2>
                      {!rightIsSub && ev.status === "live" ? <StatusDot>{t("common.status.live", "Live")}</StatusDot> : null}
                    </div>
                    <div style={{ fontSize: 12.5, color: T.soft, marginTop: 3 }}>{rightIsSub ? ev.title : /* TODO(i18n): replace template-literal with t('eventday.eventDay.text24', { ... }) */`${ev.date}${ev.time ? ` · ${ev.time}` : ""} · ${ev.location}`}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{selChecked}/{selTotal} {t("eventday.eventDay.in", "in")}</span>
                    {can("guests.checkin") ? <ScanBtn onClick={() => setCheckinOpen(true)} /> : null}
                  </div>
                </div>
                {!rightIsSub ? progressStrip : null}
                <div style={{ flex: 1, overflowY: "auto", background: T.canvas, minHeight: 0 }}>{rightBody}</div>
              </>
            )}
          </section>
        </div>
        {toast ? <ToastEl>{toast}</ToastEl> : null}
        {checkinOpen && ev ? <CheckinOverlay eventTitle={ev.title} onClose={() => setCheckinOpen(false)} onCode={checkInByScannedCode} /> : null}
        {walkupOpen && ev ? <AddGuestModal eventTitle={ev.title} bookingItems={ev.bookingQuestions ?? []} ticketTypeIds={ev.ticketTypeIds ?? []} onClose={() => setWalkupOpen(false)} onAdd={onAddGuest} /> : null}
        {detailId && ev ? (() => { const g = resolveGuest(detailId); return g ? <GuestDetailModal guest={g} bookingItems={ev.bookingQuestions ?? []} ticketTypeIds={ev.ticketTypeIds ?? []} checked={attendees.find((a) => a.id === detailId)?.checked ?? false} canCheckin={can("guests.checkin")} onToggleCheck={() => { toggleCheck(detailId); }} onSave={async (patch) => { const ok = await saveGuest(detailId, patch); if (ok) flash(`${patch.name || g.name} updated`); }} onClose={() => setDetailId(null)} /> : null; })() : null}
      </Screen>
    );
  }

  /* ══ Phone: single-column stack ═══════════════════════════════════ */
  let phoneBody: React.ReactNode;
  if (scene === "dashboard" || !ev) {
    phoneBody = (
      <>
        <Topbar right={ev && can("guests.checkin") ? <ScanBtn onClick={() => setCheckinOpen(true)} /> : undefined} />
        <div style={{ flex: 1, overflowY: "auto", background: "#fff" }}>{eventListInner}</div>
      </>
    );
  } else if (scene === "event") {
    phoneBody = (
      <>
        <Topbar title={ev.title} onBack={() => setScene("dashboard")} right={can("guests.checkin") ? <ScanBtn onClick={() => setCheckinOpen(true)} /> : undefined} />
        <div style={{ flex: 1, overflowY: "auto", background: T.canvas, paddingBottom: 16 }}>
          {progressStrip}
          {eventWorkBody}
        </div>
      </>
    );
  } else {
    const body = scene === "occupancy" ? occupancyBody : scene === "message" ? messageBody : scene === "apps" ? appsBody : scene === "app" ? appBody : queueBody;
    const onBack = scene === "queue" ? () => setScene("dashboard") : scene === "app" ? () => setScene("apps") : () => setScene("event");
    const right = scene === "queue" && can("onsite.walkup") ? <button onClick={() => setWalkupOpen(true)} style={{ ...PBTN("primary"), height: 34, fontSize: 13 }}>{t("eventday.eventDay.addGuest", "Add Guest")}</button> : undefined;
    phoneBody = (
      <>
        {scene === "app" ? null : <Topbar title={subTitle(scene)} onBack={onBack} right={right} />}
        <div style={{ flex: 1, overflowY: "auto", background: T.canvas, minHeight: 0 }}>{body}</div>
      </>
    );
  }

  return (
    <Screen rail="eventday" topbar>
      <div style={{ flex: 1, minHeight: 0, width: "100%", background: "#fff", display: "flex", flexDirection: "column" }}>{phoneBody}</div>
      {toast ? <ToastEl>{toast}</ToastEl> : null}
      {checkinOpen && ev ? <CheckinOverlay eventTitle={ev.title} onClose={() => setCheckinOpen(false)} onCode={checkInByScannedCode} /> : null}
      {walkupOpen && ev ? <AddGuestModal eventTitle={ev.title} bookingItems={ev.bookingQuestions ?? []} ticketTypeIds={ev.ticketTypeIds ?? []} onClose={() => setWalkupOpen(false)} onAdd={onAddGuest} /> : null}
      {detailId && ev ? (() => { const g = resolveGuest(detailId); return g ? <GuestDetailModal guest={g} bookingItems={ev.bookingQuestions ?? []} ticketTypeIds={ev.ticketTypeIds ?? []} checked={attendees.find((a) => a.id === detailId)?.checked ?? false} canCheckin={can("guests.checkin")} onToggleCheck={() => { toggleCheck(detailId); }} onSave={async (patch) => { const ok = await saveGuest(detailId, patch); if (ok) flash(`${patch.name || g.name} updated`); }} onClose={() => setDetailId(null)} /> : null; })() : null}
    </Screen>
  );
}

/* ── Toast ──────────────────────────────────────────────────────────── */
function ToastEl({ children }: { children: React.ReactNode }) {
  return <div style={{ position: "fixed", left: "50%", bottom: 40, transform: "translateX(-50%)", background: T.n1, color: "#fff", padding: "10px 16px", borderRadius: 8, fontSize: 13.5, fontWeight: 600, boxShadow: T.shadowLg, zIndex: 300, fontFamily: SANS }}>{children}</div>;
}

/* ── Check-in scanner — REAL camera scanner overlay (voyage#70) ─────────
 * Replaces the retired "Simulate a successful scan" placeholder. Acquires the
 * camera with getUserMedia({video:{facingMode:'environment'}}) into a live
 * <video>, decodes QR via BarcodeDetector({formats:['qr_code']}) where the
 * browser provides it, and always offers a manual ticket-code entry fallback
 * (which is ALSO the decode path on browsers without BarcodeDetector — the
 * @zxing/browser fallback needs its dependency added platform-side; declared
 * in the changelog). No simulated scans: a code only checks a guest in through
 * the real data-layer op (see checkInByScannedCode). */
function CheckinOverlay({ eventTitle, onClose, onCode }: { eventTitle: string; onClose: () => void; onCode: (code: string) => Promise<{ ok: boolean; text: string }> }) {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [cam, setCam] = useState<"starting" | "live" | "denied" | "unavailable">("starting");
  const [canDecode, setCanDecode] = useState(true);
  const [manual, setManual] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const busyRef = useRef(false);
  const lastRef = useRef<{ code: string; at: number }>({ code: "", at: 0 });

  const handleCode = async (code: string) => {
    if (busyRef.current) return;
    const now = Date.now();
    if (code === lastRef.current.code && now - lastRef.current.at < 2500) return; // same code still in view
    lastRef.current = { code, at: now };
    busyRef.current = true;
    try { setMsg(await onCode(code)); }
    finally { busyRef.current = false; }
  };

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let stopped = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setCam("unavailable"); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      } catch (err) { console.warn("camera access failed:", err); if (!stopped) setCam("denied"); return; }
      if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
      const v = videoRef.current;
      if (v) { v.srcObject = stream; try { await v.play(); } catch { /* autoplay gate — the muted+playsInline attrs cover it */ } }
      setCam("live");
      const BD = (window as unknown as { BarcodeDetector?: new (opts?: { formats?: string[] }) => { detect: (src: HTMLVideoElement) => Promise<{ rawValue?: string }[]> } }).BarcodeDetector;
      if (!BD) { setCanDecode(false); return; }
      const detector = new BD({ formats: ["qr_code"] });
      timer = setInterval(() => {
        const vid = videoRef.current;
        if (!vid || vid.readyState < 2 || busyRef.current) return;
        detector.detect(vid).then((codes) => {
          const raw = codes?.[0]?.rawValue;
          if (raw) void handleCode(String(raw));
        }).catch(() => { /* per-frame decode failures are routine */ });
      }, 350);
    })();
    return () => { stopped = true; if (timer) clearInterval(timer); stream?.getTracks().forEach((t) => t.stop()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitManual = () => { const c = manual.trim(); if (c) { setManual(""); void handleCode(c); } };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 260, background: "#0F0F12", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", color: "#fff" }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{t("eventday.eventDay.checkIn407b6542", "Check in ·")}{eventTitle}</div>
        <button onClick={onClose} aria-label={t("eventday.eventDay.closeScanner", "Close scanner")} style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "rgba(255,255,255,0.12)", color: "#fff", cursor: "pointer", fontSize: 18, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>✕</button>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18, padding: 24, minHeight: 0, overflowY: "auto" }}>
        <div style={{ width: 260, height: 260, borderRadius: 20, border: "3px solid rgba(255,255,255,0.5)", position: "relative", overflow: "hidden", background: "#000", flexShrink: 0 }}>
          <video ref={videoRef} muted playsInline autoPlay aria-label={t("eventday.eventDay.cameraPreview", "Camera preview")} style={{ width: "100%", height: "100%", objectFit: "cover", display: cam === "live" ? "block" : "none" }} />
          {cam === "live" ? <div aria-hidden="true" style={{ position: "absolute", left: 16, right: 16, height: 2, background: T.p1, top: "50%", boxShadow: `0 0 12px ${T.p1}` }} /> : null}
          {cam !== "live" ? (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: "rgba(255,255,255,0.75)", fontFamily: SANS, fontSize: 13, textAlign: "center", padding: 20, lineHeight: 1.5 }}>
              <QrGlyph size={30} />
              {cam === "starting" ? t('eventday.eventDay.text25') : cam === "denied" ? t('eventday.eventDay.text26') : t('eventday.eventDay.text27')}
            </div>
          ) : null}
        </div>
        {cam === "live" && !canDecode ? (
          <div style={{ color: "#fff", fontFamily: SANS, fontSize: 12.5, opacity: 0.8, textAlign: "center", maxWidth: 340, lineHeight: 1.5 }}>{t("eventday.eventDay.thisBrowserCanTDecodeQrCodesFromTheCameraTypeThe", "This browser can't decode QR codes from the camera — type the ticket code below to check the guest in.")}</div>
        ) : (
          <div style={{ color: "#fff", fontFamily: SANS, fontSize: 14, textAlign: "center", opacity: 0.85, maxWidth: 340 }}>{t("eventday.eventDay.pointTheCameraAtAGuestSQrTicketOrWalletPass1d423171", "Point the camera at a guest's QR ticket or wallet pass. Several coordinators can scan at once across doors.")}</div>
        )}
        {msg ? (
          <div role="status" style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", borderRadius: 8, background: msg.ok ? "rgba(31,138,91,0.28)" : "rgba(208,22,71,0.28)", border: `1px solid ${msg.ok ? T.green1 : T.red1}`, color: "#fff", fontFamily: SANS, fontSize: 13.5, fontWeight: 600, maxWidth: 360, lineHeight: 1.45 }}>
            <span aria-hidden="true" style={{ flexShrink: 0 }}>{msg.ok ? "✓" : "!"}</span> {msg.text}
          </div>
        ) : null}
        <div style={{ display: "flex", gap: 8, width: "100%", maxWidth: 360 }}>
          <input value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }} placeholder={t("eventday.eventDay.orEnterATicketCode", "Or enter a ticket code…")} aria-label={t("eventday.eventDay.ticketCode", "Ticket code")} style={{ flex: 1, height: 44, padding: "0 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.08)", color: "#fff", fontSize: 14, fontFamily: SANS, outline: "none", boxSizing: "border-box", minWidth: 0 }} />
          <button onClick={submitManual} disabled={!manual.trim()} style={{ ...PBTN("primary"), opacity: manual.trim() ? 1 : 0.5, flexShrink: 0 }}>{t("eventday.eventDay.checkIn", "Check in")}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Guest detail — view + edit registration details and booking answers ──
 * Tapping a guest card opens this. Registration fields and the event's booking
 * questions are editable; Save persists to the client overlay (a real
 * update-registration op replaces it at wiring time). */
function GuestDetailModal({ guest, bookingItems, ticketTypeIds, checked, canCheckin, onToggleCheck, onSave, onClose }: { guest: any; bookingItems: { kind: "question" | "group"; id: string }[]; ticketTypeIds: string[]; checked: boolean; canCheckin: boolean; onToggleCheck: () => void; onSave: (patch: Record<string, any>) => void; onClose: () => void }) {
  const t = useT();
  const types = useTicketTypes();
  const { groups, questions } = useQuestions();
  const assignedTypes = types.filter((t) => ticketTypeIds.includes(t.id));
  const pickTypes = assignedTypes.length ? assignedTypes : types;
  const blocks = buildBookingBlocks(bookingItems, groups(), questions());

  const [first, setFirst] = useState((guest.name || "").split(" ")[0] || "");
  const [last, setLast] = useState((guest.name || "").split(" ").slice(1).join(" "));
  const [email, setEmail] = useState(guest.email || "");
  const [phone, setPhone] = useState(guest.phone || "");
  const [company, setCompany] = useState(guest.company || "");
  const [tier, setTier] = useState(guest.ticket || "");
  const [answers, setAnswers] = useState<Record<string, any>>(guest.answers || {});
  const setA = (id: string, v: any) => setAnswers((m) => ({ ...m, [id]: v }));

  const save = () => { onSave({ name: `${first.trim()} ${last.trim()}`.trim(), email, phone, company, ticket: tier, answers }); onClose(); };
  const valid = first.trim() && email.trim() && tier;
  const meta = (label: string, value: string) => value ? <div style={{ fontSize: 12.5, color: T.soft }}>{label}: <span style={{ color: T.n1, fontWeight: 500 }}>{value}</span></div> : null;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 500, maxHeight: "92vh", background: "#fff", borderRadius: 16, display: "flex", flexDirection: "column", fontFamily: SANS, overflow: "hidden" }}>
        {/* header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={guest.name || "?"} color={guest.color || ACCENT} size={42} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.n1, display: "flex", alignItems: "center", gap: 8 }}>{guest.name || "Guest"}{guest.walkin ? <Pill tone="warn">{t("eventday.eventDay.walkIn", "Walk-in")}</Pill> : null}</div>
            <div style={{ fontSize: 12.5, color: T.soft, marginTop: 1 }}>{checked ? t('eventday.eventDay.text9') : t('eventday.eventDay.text14')}{" "}{guest.source ? /* TODO(i18n): replace template-literal with t('eventday.eventDay.text28', { ... }) */` · ${guest.source}` : ""}</div>
          </div>
          <button onClick={onClose} aria-label={t("common.actions.close", "Close")} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: T.n6, color: T.n2, cursor: "pointer", fontSize: 16, flexShrink: 0 }}>✕</button>
        </div>
        {/* body */}
        <div style={{ padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 18 }}>
          {canCheckin ? (
            <button onClick={onToggleCheck} style={{ ...PBTN(checked ? "secondary" : "primary"), width: "100%" }}>{checked ? t("eventday.eventDay.checkedInTapToUndo", "✓ Checked in — tap to undo") : t("eventday.eventDay.checkIn", "Check in")}</button>
          ) : null}

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: ACCENT_DK }}>{t("eventday.eventDay.registrationDetails", "Registration details")}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <label style={{ flex: 1 }}><span style={labelStyle}>{t("eventday.eventDay.firstName", "First name *")}</span><input value={first} onChange={(e) => setFirst(e.target.value)} style={inputStyle} /></label>
              <label style={{ flex: 1 }}><span style={labelStyle}>{t("eventday.eventDay.lastName", "Last name")}</span><input value={last} onChange={(e) => setLast(e.target.value)} style={inputStyle} /></label>
            </div>
            <label><span style={labelStyle}>{t("eventday.eventDay.email", "Email *")}</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} /></label>
            <label><span style={labelStyle}>{t("eventday.eventDay.phone", "Phone")}</span><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("eventday.eventDay.optional", "Optional")} style={inputStyle} /></label>
            <label><span style={labelStyle}>{t("eventday.eventDay.companyOrganization", "Company / organization")}</span><input value={company} onChange={(e) => setCompany(e.target.value)} placeholder={t("eventday.eventDay.optional", "Optional")} style={inputStyle} /></label>
            <div>
              <span style={labelStyle}>{t("eventday.eventDay.ticketTypef985f068", "Ticket type *")}</span>
              {pickTypes.length ? (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {pickTypes.map((tt) => { const on = tier === tt.name; return <button type="button" key={tt.id} onClick={() => setTier(tt.name)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 6, border: `1.5px solid ${on ? ACCENT : T.n4}`, background: on ? ACCENT_SOFT : "#fff", color: on ? ACCENT_DK : T.n1, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: SANS }}><span style={{ width: 8, height: 8, borderRadius: 999, background: tt.color, flexShrink: 0 }} />{tt.name}</button>; })}
                </div>
              ) : <div style={{ fontSize: 12.5, color: T.soft }}>{t("eventday.eventDay.noTicketTypesAssignedToThisEvent", "No ticket types assigned to this event.")}</div>}
            </div>
          </div>

          {blocks.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: ACCENT_DK }}>{t("eventday.eventDay.bookingQuestions", "Booking questions")}</div>
              <BookingBlocks blocks={blocks} answers={answers} setA={setA} />
            </div>
          ) : null}
        </div>
        {/* footer */}
        <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ ...PBTN("secondary"), flex: "0 0 auto", paddingLeft: 20, paddingRight: 20 }}>{t("common.actions.cancel", "Cancel")}</button>
          <button disabled={!valid} onClick={save} style={{ ...PBTN("primary"), flex: 1, opacity: valid ? 1 : 0.5 }}>{t("eventday.eventDay.saveChanges", "Save changes")}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Message body ───────────────────────────────────────────────────── */
function MessageBody({ eventId, attendees, onSent }: { eventId: string; attendees: Attendee[]; onSent: (n: number) => void }) {
  const t = useT();
  const lib = useMessaging().all().filter((m) => m.status === "live");
  const [aud, setAud] = useState<string>("all");
  const [channel, setChannel] = useState<"email" | "sms" | "whatsapp">("email");
  const [msgSel, setMsgSel] = useState<string | "custom" | null>(null); // null = none chosen
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const canIndividual = can("campaigns.send_individual");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);

  const tpl = typeof msgSel === "string" && msgSel !== "custom" ? lib.find((m) => m.id === msgSel) : null;
  const bodyFor = (t: (typeof lib)[number], ch: string) => t.perChannel?.[ch] ?? t.perChannel?.sms ?? t.perChannel?.email ?? t.body ?? "";

  const choose = (sel: string | "custom") => {
    setMsgSel(sel); setPicking(false); setQuery("");
    if (sel === "custom") { setSubject(""); setBody(""); return; }
    const t = lib.find((m) => m.id === sel); if (!t) return;
    const ch = t.channels.includes(channel) ? channel : (t.channels[0] as typeof channel) ?? channel;
    setChannel(ch); setSubject(t.subject ?? ""); setBody(bodyFor(t, ch));
  };
  const clearMsg = () => { setMsgSel(null); setSubject(""); setBody(""); };
  const pickChannel = (ch: "email" | "sms" | "whatsapp") => {
    setChannel(ch);
    if (tpl) setBody(bodyFor(tpl, ch));
  };

  // Audience: base segments + one option per ticket type present on the guest list.
  const tiers = [...new Set(attendees.map((a) => a.tier).filter(Boolean))];
  const audCount = (id: string) => {
    if (id === "all") return attendees.length;
    if (id === "checked") return attendees.filter((a) => a.checked).length;
    if (id === "waitlist") return attendees.filter((a) => !a.checked).length;
    if (id.startsWith("tier:")) { const t = id.slice(5); return attendees.filter((a) => a.tier === t).length; }
    return 0;
  };
  const baseAud = [{ id: "all", label: t("eventday.eventDay.everyone", "Everyone") }, { id: "checked", label: t("eventday.eventDay.checkedIn", "Checked-in") }, { id: "waitlist", label: t("eventday.eventDay.notYetIn", "Not yet in") }];
  const audOptions = [...baseAud, ...tiers.map((t) => ({ id: `tier:${t}`, label: t }))];
  const count = audCount(aud);

  const chip = (on: boolean): React.CSSProperties => ({ padding: "6px 14px", borderRadius: 999, border: `1.5px solid ${on ? ACCENT : T.n4}`, background: on ? ACCENT_SOFT : "#fff", color: on ? ACCENT_DK : T.n1, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: SANS });
  const pickRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: "11px 12px", border: `1px solid ${T.border}`, borderRadius: 10, background: "#fff", cursor: "pointer", fontFamily: SANS };
  const chosen = msgSel !== null;
  const chosenName = msgSel === "custom" ? "Custom message" : (tpl?.name ?? "");
  const filteredLib = lib.filter((m) => !query.trim() || m.name.toLowerCase().includes(query.toLowerCase()));
  const canSend = chosen && body.trim() && count > 0 && (channel !== "email" || subject.trim());

  return (
    <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 14, maxWidth: 640 }}>
      {/* Message — pick from the library (or write your own) via a picker */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 6 }}>{t("eventday.eventDay.message", "Message")}</div>
        {!chosen ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ padding: 16, background: T.n6, borderRadius: 8, fontSize: 13, color: T.soft }}>{t("eventday.eventDay.noMessageSelectedChooseOneFromYourLibraryOrWriteYourOwn", "No message selected. Choose one from your library, or write your own.")}</div>
            <div><button onClick={() => setPicking(true)} style={{ ...PBTN("secondary"), height: 38, fontSize: 13 }}>{t("eventday.eventDay.chooseMessage", "+ Choose message")}</button></div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 11px", border: `1px solid ${T.border}`, borderRadius: 9, background: "#fff" }}>
            <span style={{ width: 26, height: 26, borderRadius: 6, background: ACCENT_SOFT, color: ACCENT_DK, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 13 }}>✉️</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{chosenName}</span>
            <button onClick={() => setPicking(true)} style={{ border: "none", background: "transparent", color: ACCENT_DK, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: SANS }}>{t("eventday.eventDay.change", "Change")}</button>
            <button onClick={clearMsg} title={t("common.actions.remove", "Remove")} style={{ border: "none", background: "transparent", color: T.n3, cursor: "pointer", fontSize: 15, display: "inline-flex" }}>✕</button>
          </div>
        )}
      </div>

      {/* Audience — segments + per-ticket-type */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 6 }}>{t("eventday.eventDay.audience", "Audience")}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {audOptions.map((a) => <button key={a.id} onClick={() => setAud(a.id)} style={chip(aud === a.id)}>{a.label} <span style={{ opacity: 0.6, fontWeight: 500 }}>· {audCount(a.id)}</span></button>)}
        </div>
      </div>
      {/* Channel — Email / SMS / WhatsApp */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 6 }}>{t("eventday.eventDay.channel", "Channel")}</div>
        <div style={{ display: "flex", gap: 6 }}>
          {([["email", "Email"], ["sms", "SMS"], ["whatsapp", "WhatsApp"]] as const).map(([c, lbl]) => <button key={c} onClick={() => pickChannel(c)} style={chip(channel === c)}>{lbl}</button>)}
        </div>
      </div>
      {/* Subject (email only) + body — shown once a message is chosen */}
      {chosen ? (
        <>
          {channel === "email" ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 6 }}>{t("eventday.eventDay.subject", "Subject")}</div>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("eventday.eventDay.emailSubject", "Email subject")} style={{ width: "100%", height: 44, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 14, fontFamily: SANS, color: T.n1, boxSizing: "border-box", outline: "none" }} />
            </div>
          ) : null}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 6 }}>{tpl ? t('eventday.eventDay.text29') : t('eventday.eventDay.text30')}</div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder={t("eventday.eventDay.writeAQuickUpdate", "Write a quick update…")} style={{ width: "100%", padding: 12, border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 14, fontFamily: SANS, color: T.n1, boxSizing: "border-box", outline: "none", resize: "vertical", lineHeight: 1.5 }} />
          </div>
        </>
      ) : null}
      {canIndividual ? <div style={{ fontSize: 11.5, color: T.soft }}>{t("eventday.eventDay.tipOpenASingleAttendeeToMessageThemIndividually", "Tip: open a single attendee to message them individually.")}</div> : null}
      {sendErr ? <div style={{ fontSize: 12.5, color: T.red1, fontWeight: 600 }}>{sendErr}</div> : null}
      <button disabled={!canSend || sending} onClick={() => {
        // Awaited real send — success feedback (onSent → toast) only after resolve.
        void (async () => {
          if (sending) return;
          setSending(true); setSendErr(null);
          try {
            await sendOnsiteMessage(eventId, { audience: { type: aud }, channel, body: body.trim(), subject: subject.trim() || undefined, messageId: tpl?.id } as never);
            onSent(count);
          } catch (err) {
            console.warn("sendOnsiteMessage failed:", err);
            setSendErr("Couldn't send the message. Nothing went out — retry in a moment.");
          } finally { setSending(false); }
        })();
      }} style={{ ...PBTN("primary"), width: "100%", opacity: canSend && !sending ? 1 : 0.5 }}>{sending ? t("eventday.eventDay.sending", "Sending…") : t("eventday.eventDay.sendToGuestCount.icu", { count })}</button>

      {/* Picker modal — search + list, like event-creation Add questions/surveys */}
      {picking ? (
        <div onClick={() => setPicking(false)} style={{ position: "fixed", inset: 0, zIndex: 240, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 460, maxHeight: "80vh", background: "#fff", borderRadius: 16, display: "flex", flexDirection: "column", fontFamily: SANS, overflow: "hidden" }}>
            <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{t("eventday.eventDay.chooseAMessage", "Choose a message")}</div>
              <button onClick={() => setPicking(false)} aria-label={t("common.actions.close", "Close")} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: T.n6, color: T.n2, cursor: "pointer", fontSize: 15 }}>✕</button>
            </div>
            <div style={{ padding: "12px 16px 8px" }}>
              <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("eventday.eventDay.searchMessages", "Search messages…")} style={{ width: "100%", height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 14, fontFamily: SANS, color: T.n1, boxSizing: "border-box", outline: "none" }} />
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
              <button onClick={() => choose("custom")} style={pickRow}>
                <span style={{ width: 32, height: 32, borderRadius: 7, background: T.n6, color: T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 15 }}>✍️</span>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{t("eventday.eventDay.writeYourOwn", "Write your own")}</div><div style={{ fontSize: 12, color: T.soft }}>{t("eventday.eventDay.startFromABlankMessage", "Start from a blank message")}</div></div>
                <span style={{ color: ACCENT_DK, fontSize: 18 }}>+</span>
              </button>
              {filteredLib.map((m) => (
                <button key={m.id} onClick={() => choose(m.id)} style={pickRow}>
                  <span style={{ width: 32, height: 32, borderRadius: 7, background: ACCENT_SOFT, color: ACCENT_DK, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 15 }}>✉️</span>
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{m.name}</div><div style={{ fontSize: 12, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{(m.channels || []).map((c) => c.toUpperCase()).join(" · ")}</div></div>
                  <span style={{ color: ACCENT_DK, fontSize: 18 }}>+</span>
                </button>
              ))}
              {filteredLib.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("eventday.eventDay.noMessagesMatch", "No messages match “")}{query}”.</div> : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── Add Guest — a full registration, same as a customer self-booking ──
 * A coordinator registers a guest on the spot: contact details, ticket type,
 * plus the event's assigned booking questions — the same fields a customer
 * would complete themselves when registering. Multi-step so it works on a phone
 * at the door and on a laptop. Surveys are NOT collected here — they go out via
 * a communications flow before/after the event. (Interim: answers are captured
 * client-side; a real
 * register-guest op replaces the local add at wiring time.)
 */
const inputStyle = { width: "100%", height: 44, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 15, fontFamily: SANS, boxSizing: "border-box" as const, outline: "none", color: T.n1 };
const labelStyle = { fontSize: 12.5, fontWeight: 600, color: T.n2, marginBottom: 5, display: "block" };

/* A choice option list (radio/select = single; checks/multi = multi). Shown in
 * full — no per-question gating (the parent Yes/No gates the whole group). */
function ChoiceList({ options, value, onChange, multi }: { options: { id: string; label: string }[]; value: any; onChange: (v: any) => void; multi: boolean }) {
  const arr: string[] = Array.isArray(value) ? value : [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {options.map((o) => { const on = multi ? arr.includes(o.label) : value === o.label; return <button type="button" key={o.id} onClick={() => onChange(multi ? (on ? arr.filter((x) => x !== o.label) : [...arr, o.label]) : o.label)} style={{ textAlign: "left", padding: "9px 12px", borderRadius: 6, border: `1.5px solid ${on ? ACCENT : T.n4}`, background: on ? ACCENT_SOFT : "#fff", color: on ? ACCENT_DK : T.n1, fontSize: 13.5, fontWeight: on ? 600 : 500, cursor: "pointer", fontFamily: SANS, display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 16, height: 16, borderRadius: multi ? 4 : 999, border: `1.5px solid ${on ? ACCENT : T.n4}`, background: on ? ACCENT : "#fff", color: "#fff", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>{multi && on ? "✓" : ""}</span>{o.label}</button>; })}
    </div>
  );
}

function QuestionField({ q, value, onChange }: { q: Question; value: any; onChange: (v: any) => void }) {
  const t = QTYPE(q.type).id;
  const label = (
    <span style={labelStyle}>{q.label}{q.required ? <span style={{ color: T.red1, marginLeft: 3 }}>*</span> : null}
      {q.help ? <span style={{ display: "block", fontSize: 11.5, fontWeight: 400, color: T.soft, marginTop: 2 }}>{q.help}</span> : null}
    </span>
  );
  if (t === "heading") return <div style={{ fontSize: 14, fontWeight: 700, color: T.n1, marginTop: 4 }}>{q.label}</div>;
  if (t === "label") return <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>{q.label}</div>;
  if (t === "long") return <label>{label}<textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} style={{ ...inputStyle, height: "auto", padding: 12, resize: "vertical", lineHeight: 1.5 }} /></label>;
  if (t === "number") return <label>{label}<input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={inputStyle} /></label>;
  if (t === "datetime") return <label>{label}<input type="datetime-local" value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={inputStyle} /></label>;
  if (t === "file") return <label>{label}<input type="file" onChange={(e) => onChange(e.target.files?.[0]?.name ?? "")} style={{ ...inputStyle, height: "auto", padding: 10, border: `1px dashed ${T.n4}` }} /></label>;
  if (t === "yesno") return (
    <div>{label}
      <div style={{ display: "flex", gap: 6 }}>
        {["Yes", "No"].map((o) => { const on = value === o; return <button type="button" key={o} onClick={() => onChange(o)} style={{ flex: 1, padding: "9px 0", borderRadius: 6, border: `1.5px solid ${on ? ACCENT : T.n4}`, background: on ? ACCENT_SOFT : "#fff", color: on ? ACCENT_DK : T.n1, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: SANS }}>{o}</button>; })}
      </div>
    </div>
  );
  if (t === "select" || t === "radio") return <div>{label}<ChoiceList options={q.options} value={value} onChange={onChange} multi={false} /></div>;
  if (t === "checks" || t === "multi") return <div>{label}<ChoiceList options={q.options} value={value} onChange={onChange} multi={true} /></div>;
  return <label>{label}<input value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={inputStyle} /></label>;
}

function GatedGroup({ gate, items, answers, setA }: { gate: Question; items: Question[]; answers: Record<string, any>; setA: (id: string, v: any) => void }) {
  const cur = answers[gate.id] ?? "No"; // default No
  const open = cur === "Yes";
  const setGate = (v: string) => { setA(gate.id, v); if (v !== "Yes") items.forEach((c) => setA(c.id, undefined)); };
  return (
    <div>
      <span style={labelStyle}>{gate.label}{gate.required ? <span style={{ color: T.red1, marginLeft: 3 }}>*</span> : null}
        {gate.help ? <span style={{ display: "block", fontSize: 11.5, fontWeight: 400, color: T.soft, marginTop: 2 }}>{gate.help}</span> : null}
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        {["No", "Yes"].map((o) => { const on = cur === o; return <button type="button" key={o} onClick={() => setGate(o)} style={{ flex: 1, padding: "9px 0", borderRadius: 6, border: `1.5px solid ${on ? ACCENT : T.n4}`, background: on ? ACCENT_SOFT : "#fff", color: on ? ACCENT_DK : T.n1, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: SANS }}>{o}</button>; })}
      </div>
      {open ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12, paddingLeft: 14, borderLeft: `2px solid ${ACCENT_SOFT}` }}>
          {items.map((c) => <QuestionField key={c.id} q={c} value={answers[c.id]} onChange={(v) => setA(c.id, v)} />)}
        </div>
      ) : null}
    </div>
  );
}

type BQBlock = { key: string; gate: Question | null; items: Question[] };
function buildBookingBlocks(bookingItems: { kind: "question" | "group"; id: string }[], groupsList: { id: string; items: { qid: string }[] }[], questionsList: Question[]): BQBlock[] {
  const out: BQBlock[] = [];
  for (const it of bookingItems) {
    if (it.kind === "group") {
      const g = groupsList.find((x) => x.id === it.id); if (!g) continue;
      const qs = g.items.map((gi) => questionsList.find((q) => q.id === gi.qid)).filter(Boolean) as Question[];
      if (!qs.length) continue;
      if (QTYPE(qs[0].type).id === "yesno" && qs.length > 1) out.push({ key: g.id, gate: qs[0], items: qs.slice(1) });
      else out.push({ key: g.id, gate: null, items: qs });
    } else {
      const q = questionsList.find((x) => x.id === it.id); if (q) out.push({ key: q.id, gate: null, items: [q] });
    }
  }
  return out;
}
function BookingBlocks({ blocks, answers, setA }: { blocks: BQBlock[]; answers: Record<string, any>; setA: (id: string, v: any) => void }) {
  return <>{blocks.map((b) => b.gate
    ? <GatedGroup key={b.key} gate={b.gate} items={b.items} answers={answers} setA={setA} />
    : <div key={b.key} style={{ display: "flex", flexDirection: "column", gap: 14 }}>{b.items.map((q) => <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setA(q.id, v)} />)}</div>
  )}</>;
}

function AddGuestModal({ eventTitle, bookingItems, ticketTypeIds, onClose, onAdd }: { eventTitle: string; bookingItems: { kind: "question" | "group"; id: string }[]; ticketTypeIds: string[]; onClose: () => void; onAdd: (name: string, tier: string, extra: { email: string; phone: string; answers: Record<string, any> }) => void }) {
  const t = useT();
  const types = useTicketTypes();
  const { groups, questions } = useQuestions();

  // Ticket types are limited to those ASSIGNED to this event.
  const assignedTypes = types.filter((t) => ticketTypeIds.includes(t.id));
  const pickTypes = assignedTypes.length ? assignedTypes : types; // fallback if none mapped yet

  // Booking questions ASSIGNED to this event, as gated blocks (a group whose
  // FIRST question is a Yes/No gates the rest of that group).
  const blocks = buildBookingBlocks(bookingItems, groups(), questions());

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [tier, setTier] = useState(""); // must be chosen
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const setA = (id: string, v: any) => setAnswers((m) => ({ ...m, [id]: v }));

  // Visible required non-choice fields must be filled; details are required.
  const visibleQs = (): Question[] => {
    const out: Question[] = [];
    for (const b of blocks) { if (b.gate) { out.push(b.gate); if ((answers[b.gate.id] ?? "No") === "Yes") out.push(...b.items); } else out.push(...b.items); }
    return out;
  };
  const SKIP = new Set(["select", "radio", "checks", "multi", "yesno"]);
  const missingRequired = () => visibleQs().some((q) => q.required && QTYPE(q.type).value && !SKIP.has(QTYPE(q.type).id) && (() => { const v = answers[q.id]; return v == null || v === "" || (Array.isArray(v) && !v.length); })());
  const valid = first.trim() && last.trim() && email.trim() && tier && !missingRequired();

  // drop29 action #4: pass EVERYTHING the form collects — email/phone/answers
  // now persist through addWalkup (the data layer accepts all three).
  const submit = () => { onAdd(`${first.trim()} ${last.trim()}`.trim(), tier, { email: email.trim(), phone: phone.trim(), answers }); };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 500, maxHeight: "92vh", background: "#fff", borderRadius: 16, display: "flex", flexDirection: "column", fontFamily: SANS, overflow: "hidden" }}>
        {/* header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.n1 }}>{t("eventday.eventDay.addGuestd675272d", "Add guest")}</div>
            <div style={{ fontSize: 12.5, color: T.soft, marginTop: 1 }}>{eventTitle}</div>
          </div>
          <button onClick={onClose} aria-label={t("common.actions.close", "Close")} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: T.n6, color: T.n2, cursor: "pointer", fontSize: 16, flexShrink: 0 }}>✕</button>
        </div>
        {/* body — single form */}
        <div style={{ padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: ACCENT_DK }}>{t("eventday.eventDay.registrationDetails", "Registration details")}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <label style={{ flex: 1 }}><span style={labelStyle}>{t("eventday.eventDay.firstName", "First name *")}</span><input autoFocus value={first} onChange={(e) => setFirst(e.target.value)} style={inputStyle} /></label>
              <label style={{ flex: 1 }}><span style={labelStyle}>{t("eventday.eventDay.lastName2ced70ad", "Last name *")}</span><input value={last} onChange={(e) => setLast(e.target.value)} style={inputStyle} /></label>
            </div>
            <label><span style={labelStyle}>{t("eventday.eventDay.email", "Email *")}</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("eventday.eventDay.guestEmailCom", "guest@email.com")} style={inputStyle} /></label>
            <label><span style={labelStyle}>{t("eventday.eventDay.phone", "Phone")}</span><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("eventday.eventDay.optional", "Optional")} style={inputStyle} /></label>
            <div>
              <span style={labelStyle}>{t("eventday.eventDay.ticketType", "Ticket type")}<span style={{ color: T.red1 }}>*</span></span>
              {pickTypes.length ? (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {pickTypes.map((tt) => { const on = tier === tt.name; return <button type="button" key={tt.id} onClick={() => setTier(tt.name)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 6, border: `1.5px solid ${on ? ACCENT : T.n4}`, background: on ? ACCENT_SOFT : "#fff", color: on ? ACCENT_DK : T.n1, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: SANS }}><span style={{ width: 8, height: 8, borderRadius: 999, background: tt.color, flexShrink: 0 }} />{tt.name}</button>; })}
                </div>
              ) : <div style={{ fontSize: 12.5, color: T.soft, padding: "8px 0" }}>{t("eventday.eventDay.noTicketTypesAssignedToThisEvent", "No ticket types assigned to this event.")}</div>}
            </div>
          </div>

          {blocks.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: ACCENT_DK }}>{t("eventday.eventDay.bookingQuestions", "Booking questions")}</div>
              <BookingBlocks blocks={blocks} answers={answers} setA={setA} />
            </div>
          ) : null}
        </div>
        {/* footer */}
        <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ ...PBTN("secondary"), flex: "0 0 auto", paddingLeft: 20, paddingRight: 20 }}>{t("common.actions.cancel", "Cancel")}</button>
          <button disabled={!valid} onClick={submit} style={{ ...PBTN("primary"), flex: 1, opacity: valid ? 1 : 0.5 }}>{i18nT("eventday.eventDay.registerAmpCheckIn.rendered", "Register & check in")}</button>
        </div>
      </div>
    </div>
  );
}
